import { clearClientSession, getClientToken } from "./authSession";
import {
  crptResponseUsable,
  crptResponseRank,
  markingCodeCrptCandidates,
} from "./utils/markingScan";

const getToken = () => getClientToken();

/** Текст ошибки из ответа FastAPI (detail строка или объект с message/upstream). */
function formatApiErrorDetail(data, res) {
  const detail = data?.detail;
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const parts = [];
    if (typeof detail.message === "string" && detail.message)
      parts.push(detail.message);
    const up = detail.upstream;
    if (up && typeof up === "object") {
      const httpStatus = Number(up.http_status);
      if (httpStatus === 429) {
        parts.push(
          "Wildberries временно ограничил частоту запросов по вашему кабинету. " +
            "Подождите 1–2 минуты и отсканируйте код снова. " +
            "Если на сборке несколько человек — сканируйте по очереди.",
        );
      } else {
        if (up.url) parts.push(`URL: ${up.url}`);
        if (up.http_status != null) parts.push(`HTTP upstream: ${up.http_status}`);
        if (typeof up.body === "string" && up.body.trim())
          parts.push(`Ответ WB:\n${up.body}`);
      }
    }
    if (parts.length) return parts.join("\n\n");
  }
  return data?.message || res.statusText;
}

export async function api(url, options = {}) {
  const token = getToken();
  const headers = { "Content-Type": "application/json", ...options.headers };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) {
      clearClientSession();
    }
    throw new Error(formatApiErrorDetail(data, res));
  }
  return data;
}

export async function loginByPin(pin) {
  const res = await fetch("/api/login/pin", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ pin: String(pin) }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.detail || res.statusText);
  return data;
}

export const documentsApi = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.page != null) q.set("page", params.page);
    if (params.page_size != null) q.set("page_size", params.page_size);
    if (params.query != null && params.query !== "")
      q.set("query", params.query);
    if (params.status != null && params.status !== "")
      q.set("status", params.status);
    if (params.date_from) q.set("date_from", params.date_from);
    if (params.date_to) q.set("date_to", params.date_to);
    if (params.is_correction != null)
      q.set("is_correction", params.is_correction);
    return api(`/api/documents?${q.toString()}`);
  },
  getData: (id) => api(`/api/documents/${encodeURIComponent(id)}/data`),
};

/** Поступления: документы со статусом 10 (В обработке), с кнопкой «Подтвердить получение». */
export const receiptsApi = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.page != null) q.set("page", params.page);
    if (params.page_size != null) q.set("page_size", params.page_size);
    return api(`/api/documents/receipts?${q.toString()}`);
  },
  confirmReceipt: (documentId) =>
    api(`/api/documents/${encodeURIComponent(documentId)}/confirm-receipt`, {
      method: "POST",
    }),
};

/** Товары WB из БД. */
export const productsApi = {
  list: (params = {}) => {
    const q = new URLSearchParams();
    if (params.page != null) q.set("page", params.page);
    if (params.page_size != null) q.set("page_size", params.page_size);
    if (params.connection_id != null)
      q.set("connection_id", params.connection_id);
    if (params.search != null && params.search !== "")
      q.set("search", params.search);
    return api(`/api/products?${q.toString()}`);
  },
};

async function apiForm(url, formData) {
  const token = getToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(url, { method: "POST", headers, body: formData });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) clearClientSession();
    throw new Error(formatApiErrorDetail(data, res));
  }
  return data;
}

/** Списание брака на складе (раздел «Брак» на клиенте). */
export const defectApi = {
  previewScan: (body) =>
    api("/api/defect/preview-scan", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  acceptScan: ({ code, quantity = 1, reason, photo }) => {
    const fd = new FormData();
    fd.append("code", String(code));
    fd.append("quantity", String(quantity));
    if (reason) fd.append("reason", String(reason));
    fd.append("photo", photo, photo.name || "defect.jpg");
    return apiForm("/api/defect/accept-scan", fd);
  },
  history: (days = 90, limit = 30) => {
    const q = new URLSearchParams();
    q.set("days", String(days));
    q.set("limit", String(limit));
    return api(`/api/defect/history?${q.toString()}`);
  },
};

/** Приём возврата на склад (только из раздела «Приём возврат» на клиенте). */
export const returnsApi = {
  acceptScan: (body) =>
    api("/api/returns/accept-scan", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  history: (days = 90, limit = 30) => {
    const q = new URLSearchParams();
    q.set("days", String(days));
    q.set("limit", String(limit));
    return api(`/api/returns/history?${q.toString()}`);
  },
};

/** Сборки: товары на сборке (confirm), сгруппировано по nm_id. */
export const assembliesApi = {
  /** Только число сборочных строк — для бейджа в главном меню. */
  summary: () => api("/api/assemblies/summary"),
  onAssembly: () => api("/api/assemblies/on-assembly"),
  markingLines: (connectionId = null) => {
    const q = new URLSearchParams();
    if (connectionId != null && connectionId !== "") q.set("connection_id", String(connectionId));
    const s = q.toString();
    return api(`/api/assemblies/marking-lines${s ? `?${s}` : ""}`);
  },
  applyMarking: (body) =>
    api("/api/assemblies/marking/apply", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  resolveSticker: (body) =>
    api("/api/assemblies/marking/resolve-sticker", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  start: (body) =>
    api("/api/assemblies/start", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  finish: (body) =>
    api("/api/assemblies/finish", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** История успешных сканов по дням (текущий пользователь). */
  scanHistory: (params = {}) => {
    const q = new URLSearchParams();
    if (params.days != null) q.set("days", params.days);
    const s = q.toString();
    return api(`/api/assemblies/scan-history${s ? `?${s}` : ""}`);
  },
};

/**
 * Сборки Ozon: очередь сборки FBS, маркировка ЧЗ → экземпляры Ozon, сборка и этикетки.
 * Отличие от WB: КИЗ уходят ДО сборки, и Ozon принимает только полный набор кодов
 * по отправлению — поэтому applyMarking отправляет все отсканированные коды целиком.
 */
export const ozonAssembliesApi = {
  /** Только число отправлений к сборке — для бейджа. */
  summary: (connectionId = null) => {
    const q = new URLSearchParams();
    if (connectionId != null && connectionId !== "") q.set("connection_id", String(connectionId));
    const s = q.toString();
    return api(`/api/ozon-assemblies/summary${s ? `?${s}` : ""}`);
  },
  onAssembly: (connectionId = null) => {
    const q = new URLSearchParams();
    if (connectionId != null && connectionId !== "") q.set("connection_id", String(connectionId));
    const s = q.toString();
    return api(`/api/ozon-assemblies/on-assembly${s ? `?${s}` : ""}`);
  },
  markingLines: (connectionId) =>
    api(`/api/ozon-assemblies/marking-lines?connection_id=${encodeURIComponent(connectionId)}`),
  /** Скан штрихкода отправления (barcodes.upper/lower_barcode или scanit). */
  resolveBarcode: (body) =>
    api("/api/ozon-assemblies/marking/resolve-barcode", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /**
   * Хаотичный скан: кладовщик сканирует КИЗ в любом порядке, сервер сам определяет
   * товар по GTIN и привязывает код к отправлению с ближайшим дедлайном.
   */
  scan: (body) =>
    api("/api/ozon-assemblies/marking/scan", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Снять код (отмена заказа): по scan_id или все коды отправления. */
  unscan: (body) =>
    api("/api/ozon-assemblies/marking/unscan", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  applyMarking: (body) =>
    api("/api/ozon-assemblies/marking/apply", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  ship: (body) =>
    api("/api/ozon-assemblies/ship", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Этикетки отправлений: PDF появляется только после сборки (awaiting_deliver). */
  labels: (body) =>
    api("/api/ozon-assemblies/labels", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};

/**
 * Сборки Яндекс Маркета: очередь FBS/Экспресс, КИЗ → раскладка по коробкам,
 * «Готов к отгрузке» и ярлыки. Как на Ozon, коды уходят полным набором по заказу:
 * PUT .../boxes каждый раз целиком заменяет раскладку.
 */
export const ymAssembliesApi = {
  /** Только число заказов к сборке — для бейджа. */
  summary: (connectionId = null) => {
    const q = new URLSearchParams();
    if (connectionId != null && connectionId !== "") q.set("connection_id", String(connectionId));
    const s = q.toString();
    return api(`/api/ym-assemblies/summary${s ? `?${s}` : ""}`);
  },
  onAssembly: (connectionId = null) => {
    const q = new URLSearchParams();
    if (connectionId != null && connectionId !== "") q.set("connection_id", String(connectionId));
    const s = q.toString();
    return api(`/api/ym-assemblies/on-assembly${s ? `?${s}` : ""}`);
  },
  markingLines: (connectionId) =>
    api(`/api/ym-assemblies/marking-lines?connection_id=${encodeURIComponent(connectionId)}`),
  /** Хаотичный скан: сервер сам находит позицию по GTIN, ближайший дедлайн — первым. */
  scan: (body) =>
    api("/api/ym-assemblies/marking/scan", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Снять код (не прошёл проверку, отмена заказа): по scan_id или все коды заказа. */
  unscan: (body) =>
    api("/api/ym-assemblies/marking/unscan", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Раскладка с КИЗ в Маркет + ожидание проверки кодов. */
  applyMarking: (body) =>
    api("/api/ym-assemblies/marking/apply", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  checkMarking: (connectionId, orderId) =>
    api(
      `/api/ym-assemblies/marking/check?connection_id=${encodeURIComponent(connectionId)}` +
        `&order_id=${encodeURIComponent(orderId)}`,
    ),
  /** PROCESSING/READY_TO_SHIP — только после раскладки и проверки кодов. */
  ship: (body) =>
    api("/api/ym-assemblies/ship", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  /** Ярлыки заказа: сервер отдаёт PDF Маркета, на клиенте — blob URL. */
  labels: async (connectionId, orderId) => {
    const token = getToken();
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await fetch(
      `/api/ym-assemblies/labels?connection_id=${encodeURIComponent(connectionId)}` +
        `&order_id=${encodeURIComponent(orderId)}`,
      { headers },
    );
    if (!res.ok) {
      if (res.status === 401) clearClientSession();
      const data = await res.json().catch(() => ({}));
      throw new Error(formatApiErrorDetail(data, res));
    }
    return URL.createObjectURL(await res.blob());
  },
};

function crptMobileApiOrigin() {
  const fromEnv = process.env.REACT_APP_CRPT_MOBILE_API;
  if (fromEnv && String(fromEnv).trim())
    return String(fromEnv).replace(/\/$/, "");
  if (typeof window !== "undefined") {
    const h = window.location.hostname;
    if (h === "localhost" || h === "127.0.0.1") return "/crpt-mobile";
  }
  return "https://mobile.api.crpt.ru";
}

/**
 * На продакшене прямой fetch к mobile.api.crpt.ru из браузера блокируется CORS.
 * Тогда запрос идёт через GET /api/marking/mobile-check (тот же ответ JSON).
 * REACT_APP_CRPT_USE_PROXY=true — всегда через сервер; false — всегда из браузера (только если CORS разрешён).
 */
function shouldUseCrptServerProxy() {
  const v = process.env.REACT_APP_CRPT_USE_PROXY;
  if (v === "1" || v === "true") return true;
  if (v === "0" || v === "false") return false;
  if (typeof window === "undefined") return true;
  const h = window.location.hostname;
  return h !== "localhost" && h !== "127.0.0.1";
}

/** "server" — прокси FastAPI; "browser" — прямой mobile API (или dev-прокси /crpt-mobile). */
export function getCrptMobileCheckMode() {
  return shouldUseCrptServerProxy() ? "server" : "browser";
}

/**
 * Один GET mobile/check (серверный прокси или напрямую).
 */
async function crptMobileCheckOnce(code) {
  if (shouldUseCrptServerProxy()) {
    const q = new URLSearchParams({ code: String(code) }).toString();
    return api(`/api/marking/mobile-check?${q}`);
  }

  const base = crptMobileApiOrigin();
  const q = new URLSearchParams({ code: String(code) }).toString();
  const res = await fetch(`${base}/mobile/check?${q}`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(
      (text && text.slice(0, 200)) || "Некорректный ответ mobile API",
    );
  }
  if (!res.ok) {
    const msg = data?.message || data?.error || text || res.statusText;
    throw new Error(typeof msg === "string" ? msg : res.statusText);
  }
  return data;
}

/**
 * Публичный mobile API Честного знака: GET /mobile/check?code=…
 * На проде — через сервер. Для длинных КМ пробует укороченный вариант до AI 91.
 */
export async function crptMobileCheck(code) {
  if (shouldUseCrptServerProxy()) {
    return crptMobileCheckOnce(code);
  }
  const candidates = markingCodeCrptCandidates(code);
  if (!candidates.length) return crptMobileCheckOnce(code);
  let last = null;
  let bestRank = -1;
  for (const candidate of candidates) {
    const data = await crptMobileCheckOnce(candidate);
    last = data;
    const rank = crptResponseRank(data);
    if (rank > bestRank) bestRank = rank;
    if (rank >= 3) return data;
    if (crptResponseUsable(data) && bestRank < 2) return data;
  }
  return last;
}

/** Проверка КМ: True API через сервер (токен режима) или legacy mobile API. */
export const markingApi = {
  codesCheck: (code) =>
    api("/api/marking/codes-check", {
      method: "POST",
      body: JSON.stringify({ code: String(code) }),
    }),
  /** True API v3: cises/info + aggregated/list, токен из настроек режима на сервере. */
  trueApiResolve: (code) =>
    api("/api/marking/true-api/resolve", {
      method: "POST",
      body: JSON.stringify({ code: String(code) }),
    }),
  crptMobileCheck: (code) => crptMobileCheck(code),
  /** Тест: ЧЗ + сохранение полного JSON на сервер (data/crpt_test_scans). */
  testScan: (body) =>
    api("/api/marking/test-scan", {
      method: "POST",
      body: JSON.stringify(body),
    }),
};
